/*
 *
 * FormattedPrice
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import { KompisPriceModule } from '@inter-ikea-kompis/react-components';
import { SkapaTheme } from '@inter-ikea-kompis/themes/lib';
import { KompisDefaultFields } from '@inter-ikea-kompis/services';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { PriceModuleProductLinkEnum } from '@inter-ikea-kompis/component-price-module';
import _ from 'lodash';
import { pricingRulesSelector, cwtTranlationSelector, applicationSettingsSelector, translationsSelector, productServiceSelector } from 'domain/LanguageProvider/selectors';
import { fromJS } from 'immutable';
import { convertMeterPriceToFeetPrice, convertSquareMeterPriceToSquareFeetPrice } from 'domain/Options/utils/convertMeasurement';
import { applicationModeSelector } from 'Application/selectors';
import { ipexGalleryEnabled, kioskEnabled, upptackaEnabled } from 'app';
import { useImperialUnitMeasurementsSelector } from 'domain/Options/selectors';
import { KIOSK } from 'Application/constants';

const updatePriceForImperialUnits = (product, priceKey, isCustomValue, isSquareMeter) => {
  const price = product.getIn(['product', 'content', 'priceInformation', 'salesPrice', 0, priceKey]);
  if (price && isCustomValue) {
    let pricePerFeet;
    if(isSquareMeter){
      pricePerFeet = convertSquareMeterPriceToSquareFeetPrice(price);
    } else {
      pricePerFeet = convertMeterPriceToFeetPrice(price);
    }
    product = product.setIn(['product', 'content', 'priceInformation', 'salesPrice', 0, priceKey], pricePerFeet);
    return { product, isModified: true };
  }

  return { product, isModified: false };
};

const modifyShoppingProductsIfImperialMsmt = (shoppingProducts, useImperialUnits, isCustomValue, isSquareMeter) => {
  if (!shoppingProducts) {
    return [];
  }
  let immutableProducts = fromJS(shoppingProducts);
  let isModified = false;
  if (immutableProducts.size > 0) {
    immutableProducts = immutableProducts.map(product => {
      if (useImperialUnits) {
        const keysToUpdate = ['priceInclTax', 'priceExclTax', 'indicativeTotalTax'];
        keysToUpdate.forEach(key => {
          const result = updatePriceForImperialUnits(product, key, isCustomValue, isSquareMeter);
          product = result.product;
          if (result.isModified) {
            isModified = true;
          }
        });
      }
      return product;
    });
  }

  return isModified ? immutableProducts.toJS() : shoppingProducts;
}

const removeNegativeDuplicatesAnd0Quantity = (shoppingProducts) => {
  let negativeItem;
  // Filter out items with negative or zero quantity
  let shoppingProdFiltered = shoppingProducts.filter(item => {
    if (item.quantity < 0) {
      negativeItem = item;
      return false;
    } else if (item.quantity === 0) {
      return false;
    } else {
      return true;
    }
  });
  // Adjust quantities for negative items
  if (negativeItem) {
    shoppingProdFiltered = shoppingProdFiltered.map(item => {
      if (item.id === negativeItem.id) {
        item.quantity += negativeItem.quantity;
      }
      return item;
    });
  }
  // Remove duplicates and sum their quantities
  let shoppingProdFinishedFiltering = [];
  shoppingProdFiltered.forEach(product => {
    const prodCopy = { ...product };
    const duplicateIndex = shoppingProdFinishedFiltering.findIndex(prod => prod.id === prodCopy.id);
    if (duplicateIndex >= 0) {
      shoppingProdFinishedFiltering[duplicateIndex].quantity += prodCopy.quantity;
    } else {
      shoppingProdFinishedFiltering.push(prodCopy);
    }
  });
  return shoppingProdFinishedFiltering;
};

export class FormattedPrice extends React.PureComponent { // eslint-disable-line react/prefer-stateless-function
	async loadServiceData(shoppingProducts, productService) {
    const filteredShoppingProducts = removeNegativeDuplicatesAnd0Quantity(shoppingProducts);
    const hasProduct = filteredShoppingProducts && filteredShoppingProducts.length > 0;
		this.setState({
      shoppingProductsContent: hasProduct ? await productService.getShoppingProducts(filteredShoppingProducts, KompisDefaultFields) : [],
			shoppingProductArtNos: shoppingProducts
		});
	}
  state = {
    shoppingProductsContent: null,
    shoppingProductArtNos: null,
		visibleModal: true,
	};

  componentDidMount() {
    if(this.props.shoppingProducts){
      this.loadServiceData(this.props.shoppingProducts, this.props.productService);
    }
  }

  render() {
    const { applicationSettings, productService, translations, shoppingProducts, layout, showPriceExclTax, showPriceInclTax, showEcoFee, noInfoTextNeeded, showPriceUnit, productName, description, showLowestPreviousSalesPrice, isProductCard, isSink, children, useImperialUnits, isCustomValue, isSquareMeter, applicationMode } = this.props;
    const { shoppingProductsContent, visibleModal, shoppingProductArtNos } = this.state;
    
    if (shoppingProducts && shoppingProductArtNos && (shoppingProducts != shoppingProductArtNos)) {
      this.loadServiceData(shoppingProducts, productService);
    }

    const shoppingProductsModified = modifyShoppingProductsIfImperialMsmt(shoppingProductsContent, useImperialUnits, isCustomValue, isSquareMeter);
    const isKiosk = applicationMode === KIOSK;
    const isInKioskMode = ipexGalleryEnabled || upptackaEnabled || isKiosk ? true : false;
    console.log('isInKioskMode', isInKioskMode);
    const isSingleItem = shoppingProductsContent && shoppingProductsContent.length === 1;

    return (
        <KompisPriceModule
          settings={applicationSettings}
          shoppingProducts={shoppingProductsModified}
          translations={translations.kompis}
          theme={SkapaTheme}
          visibleModal={visibleModal}
          onModalOpen={event => this.setState({ visibleModal: event.detail.visibleModal })}
          onModalClose={() => this.setState({ visibleModal: null })}
          layout={layout}
          animated={true}
          showPriceExclTax={showPriceExclTax}
          showPriceUnit={(isSingleItem && !useImperialUnits) ? true : false} // Adds the unit to the decentralized (square meter) price (example: €/m²), not for linear meter
          isCombinedProduct={false} // new lower price is shown, even if only part of the products has lower price (not part of legal requirements)
          showLowestPreviousSalesPrice={isSingleItem ? true : false} // should only be true, if it's a single item
          productName={productName} //EKBACKEN, KASKER etc
          disableLinks={isInKioskMode}
          // productLink={PriceModuleProductLinkEnum.productInformationPage} // already has Sidesheet integrated into CWTC, so not a necessity. Also adds another step/complication to the keyboard 
          showOfferMessage={isSingleItem ? true : false}
          showEcoFee={showEcoFee}
        > {children}</KompisPriceModule>
    );
  }
}

FormattedPrice.propTypes = {
  useImperialFormat: PropTypes.bool,
  rules: PropTypes.object.isRequired,
  value: PropTypes.number,
  disabled: PropTypes.bool,
};

FormattedPrice.defaultProps = {
  value: 0,
  disabled: false,
};

const mapStateToProps = createStructuredSelector({
  cwtTranslation: cwtTranlationSelector,
  rules: pricingRulesSelector,
  applicationSettings: applicationSettingsSelector,
  translations: translationsSelector,
  applicationMode: applicationModeSelector,
  productService: productServiceSelector,
  useImperialUnits: useImperialUnitMeasurementsSelector,
});

export default connect(mapStateToProps)(FormattedPrice);
